import { useEffect, useState } from "react";
import { useGetRequestQuery } from "../../../../Hooks/useGetRequestQuery";
import DataTable from "react-data-table-component";
import { Link } from "react-router-dom";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { useMutationRequestQuery } from "../../../../Hooks/useMutationRequestQuery";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import { deleteLeadSourceService, getAllLeadSourcesService } from "../../../../Services/CRMAdminServices/CRMLeadSourceService";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";

const customStyles = {
    head: {
        style: {
            fontWeight: '800',
            color: '#0000ff',
            fontSize: '14px'
        },
    },
};

const CRMLeadSource = () => {
    const [leadSources, setLeadSources] = useState<any>([]);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
    const queryClient = useQueryClient();
    const navigate = useSafeNavigate();

    const { data: leadSourcesData, isLoading, isError } = useGetRequestQuery<any>(
        ['getAllLeadSources'],
        getAllLeadSourcesService,
        { enabled: true }
    );

    useEffect(() => {
        if (leadSourcesData && !isError && !isLoading) {
            setLeadSources(leadSourcesData?.data?.data || []);
        }
    }, [leadSourcesData, isError, isLoading]);

    const { mutate: deleteLeadSource, isPending: isDeleting } = useMutationRequestQuery(
        "deleteLeadSource",
        deleteLeadSourceService,
        {
            onSuccess: () => {
                toast.success("Lead source deleted successfully.");
                queryClient.invalidateQueries({ queryKey: ["getAllLeadSources"] });
                setDeleteModal(false);
            },
            onError: () => {
                toast.error("Failed to delete lead source.");
                setDeleteModal(false);
            },
        }
    );

    const confirmDelete = (id: number) => {
        setSelectedSourceId(id);
        setDeleteModal(true);
    };

    const handleDeleteClick = () => {
        if (selectedSourceId) {
            deleteLeadSource(selectedSourceId);
        }
    };

    const columns = [
        {
            name: "Action",
            cell: (row: any) => (
                <ul className="action simple-list flex-row">
                    <li className="edit">
                        <Link to={`/EditLeadSource/${row.leadSourceId}`}>
                            <i className="icon-pencil-alt" />
                        </Link>
                    </li>
                    <li className="delete">
                        <a onClick={() => confirmDelete(row.leadSourceId)} href="#">
                            <i className="icon-trash" />
                        </a>
                    </li>
                </ul>
            ),
            width: '8rem'
        },
        {
            name: "Source",
            selector: (row: any) => row.leadSource,
            sortable: false, 
            width: '30rem'
        },
        // {
        //     name: "Commission",
        //     selector: (row: any) => <span style={{ fontWeight: "bold" }}>{row.commission}</span>,
        //     sortable: false,
        //     width: '10rem'
        // }
    ];

    return (
        <div className="px-4 pt-4" style={{ minHeight: '500px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <div style={{ borderRadius: '8px', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', padding: '10px', backgroundColor: '#fff' }}>
                <div className="g-3 mb-2 d-flex justify-content-between align-items-center">
                    <h3 className="f-w-900 heading-color mx-3 py-2">Lead Sources List</h3>
                    <Button color="primary" className="mx-3" onClick={() => navigate("/AddLeadSource")}>Add Lead Source</Button>
                </div>
                {isLoading ? (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <Spinner color="primary" />
                    </div>
                ) : leadSources.length !== 0 ? (
                    <DataTable
                        columns={columns}
                        data={leadSources}
                        customStyles={customStyles}
                        responsive
                    />
                ) : (
                    <div
                        style={{
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                            padding: '12px',
                            backgroundColor: '#fff',
                            textAlign: 'center',
                        }}
                        className='mt-4 mb-2'
                    >
                        <h6>No Lead Sources data available.</h6>
                    </div>
                )}
            </div>
            <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered>
                <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Confirm Delete</ModalHeader>
                <ModalBody>Are you sure you want to delete this lead source?</ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={() => setDeleteModal(false)}>Cancel</Button>
                    <Button color="danger" onClick={handleDeleteClick} disabled={isDeleting}>
                        {isDeleting ? "Deleting..." : "Delete"}
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default CRMLeadSource;
